// Desk (FD §21) — everything waiting on the user, grouped by kind. Plain
// approvals get Approve/Reject right on the row; questions and reviews open
// the full ApprovalPanel in place. `?id=` opens an item so Home and run pages
// can link straight to it.

import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { isApprovalActionUnchanged, type Approval } from '@goblic/shared'
import { approvalsApi } from '../lib/api-client'
import { ApprovalPanel, approvalHeadline, type AgentQuestionAnswer } from '../components/approval-panel'
import { statusTone } from '../components/status-badge'
import {
  EmptyState,
  ErrorNote,
  LoadingRows,
  PageHeader,
} from '../components/ui'
import { useWorkspaceId } from '../lib/auth'
import { useLookups } from '../hooks/useLookups'
import { formatRelative } from '../lib/format'
import { RoleAvatar } from '../components/goblin-art'

export function ApprovalsPage() {
  const workspaceId = useWorkspaceId()
  const queryClient = useQueryClient()
  const lookups = useLookups()
  const search = useSearch({ from: '/_authenticated/approvals' })
  const navigate = useNavigate({ from: '/approvals' })

  const approvalsQuery = useQuery({
    queryKey: ['approvals', workspaceId, 'pending'],
    queryFn: () => approvalsApi.list({ workspaceId, status: 'pending' }),
    enabled: workspaceId !== '',
    // TODO: subscribe instead of polling — a workspace-wide approvals push
    // channel isn't scaffolded yet (routes/events/sse.ts is per-run only).
    refetchInterval: 15_000,
  })

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['approvals', workspaceId] })
  }

  const approveMutation = useMutation({
    mutationFn: ({
      id,
      actionHash,
      note,
    }: {
      id: string
      actionHash: string
      note?: string
    }) => approvalsApi.approve(id, { actionHash, note }),
    onSuccess: invalidate,
  })
  const rejectMutation = useMutation({
    mutationFn: ({
      id,
      actionHash,
      note,
    }: {
      id: string
      actionHash: string
      note?: string
    }) => approvalsApi.reject(id, { actionHash, note }),
    onSuccess: invalidate,
  })
  const instructMutation = useMutation({
    mutationFn: ({
      id,
      actionHash,
      instruction,
    }: {
      id: string
      actionHash: string
      instruction: string
    }) => approvalsApi.instruct(id, { actionHash, instruction }),
    onSuccess: invalidate,
  })
  const answerMutation = useMutation({
    mutationFn: ({
      id,
      actionHash,
      answer,
    }: {
      id: string
      actionHash: string
      answer: AgentQuestionAnswer
    }) => approvalsApi.answer(id, { actionHash, ...answer }),
    onSuccess: invalidate,
  })

  const isSubmitting =
    approveMutation.isPending ||
    rejectMutation.isPending ||
    instructMutation.isPending ||
    answerMutation.isPending
  const approvals = approvalsQuery.data?.items ?? []
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(search.id ? [search.id] : []))
  const [changed, setChanged] = useState<Set<string>>(() => new Set())
  const deepLinked = useRef<HTMLLIElement | null>(null)

  useEffect(() => {
    if (search.id) {
      setExpanded((prev) => (prev.has(search.id as string) ? prev : new Set(prev).add(search.id as string)))
    }
  }, [search.id])
  useEffect(() => {
    deepLinked.current?.scrollIntoView({ block: 'nearest' })
  }, [approvalsQuery.isSuccess])

  function toggle(id: string): void {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  /** Quick decision from the row. The full panel re-checks the same hash; if the action changed, open it for review. */
  async function quickDecide(approval: Approval, decision: 'approve' | 'reject'): Promise<void> {
    const unchanged = await isApprovalActionUnchanged(approval.action, approval.actionHash)
    if (!unchanged) {
      setChanged((prev) => new Set(prev).add(approval.id))
      setExpanded((prev) => new Set(prev).add(approval.id))
      return
    }
    const mutation = decision === 'approve' ? approveMutation : rejectMutation
    await mutation.mutateAsync({ id: approval.id, actionHash: approval.actionHash })
  }

  const groups = GROUPS.map((group) => ({
    ...group,
    items: approvals.filter((a) => groupOf(a) === group.key),
  })).filter((group) => group.items.length > 0)
  const oldest = approvals.reduce<Approval | undefined>(
    (acc, a) => (!acc || a.createdAt < acc.createdAt ? a : acc),
    undefined,
  )

  return (
    <div>
      <PageHeader
        title="Desk"
        description="Agents stop and ask before risky steps: pushing code, opening pull requests, new network access or going over budget. Questions and reviews land here too."
      />

      {approvalsQuery.isLoading ? (
        <div className="panel">
          <LoadingRows />
        </div>
      ) : approvalsQuery.isError ? (
        <div className="panel">
          <ErrorNote error={approvalsQuery.error} what="your desk" />
        </div>
      ) : approvals.length === 0 ? (
        <div className="panel">
          <EmptyState title="Nothing is waiting on you">
            When an agent needs a decision, an answer or a review it pauses its
            run and the request shows up here.
          </EmptyState>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <p className="panel flex flex-wrap items-center gap-x-6 gap-y-1 px-4 py-2.5 text-sm text-ink-3">
            <span>
              <strong className="text-lg font-semibold text-ink">{approvals.length}</strong>{' '}
              waiting
            </span>
            {groups.map((group) => (
              <span key={group.key}>
                {group.items.length} {group.label.toLowerCase()}
              </span>
            ))}
            {oldest ? (
              <span className="sm:ml-auto">Oldest: {formatRelative(oldest.createdAt)}</span>
            ) : null}
          </p>

          {groups.map((group) => (
            <section key={group.key} aria-labelledby={`desk-${group.key}`}>
              <h2
                id={`desk-${group.key}`}
                className="mb-2 flex items-baseline gap-2 text-xs font-semibold uppercase tracking-wider text-ink-2"
              >
                {group.heading}
                <span className="font-mono font-normal text-ink-3">{group.items.length}</span>
              </h2>
              <ul className="panel divide-y divide-line overflow-hidden">
                {group.items.map((approval) => {
                  const open = expanded.has(approval.id)
                  const tone = statusTone(approval.riskIndicator)
                  const quick = group.key === 'approval'
                  const panelId = `desk-panel-${approval.id}`
                  return (
                    <li
                      key={approval.id}
                      ref={approval.id === search.id ? deepLinked : undefined}
                      className="px-4 py-3"
                    >
                      <div className="flex flex-wrap items-center gap-3">
                        <RoleAvatar
                          roleName={lookups.agentRoleName(approval.agentId)}
                          size={32}
                        />
                        <div className="min-w-0 flex-1 basis-64">
                          <p className="truncate text-base font-medium text-ink">
                            {approvalHeadline(approval)}
                          </p>
                          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-sm text-ink-3">
                            <span className="truncate">
                              {approval.agentId
                                ? lookups.agentName(approval.agentId)
                                : approval.taskId
                                  ? lookups.taskTitle(approval.taskId)
                                  : 'Workflow'}
                              , {formatRelative(approval.createdAt)}
                            </span>
                            {tone === 'danger' ? (
                              <span className="rounded-full bg-danger-soft px-2 text-xs font-semibold text-danger">
                                High risk
                              </span>
                            ) : null}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {quick ? (
                            <>
                              <button
                                type="button"
                                className="btn-primary btn-sm"
                                disabled={isSubmitting}
                                onClick={() => void quickDecide(approval, 'approve')}
                              >
                                Approve
                              </button>
                              <button
                                type="button"
                                className="btn-danger-quiet btn-sm"
                                disabled={isSubmitting}
                                onClick={() => void quickDecide(approval, 'reject')}
                              >
                                Reject
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="btn-primary btn-sm"
                              aria-expanded={open}
                              aria-controls={panelId}
                              onClick={() => toggle(approval.id)}
                            >
                              {group.key === 'question' ? 'Answer' : 'Review'}
                            </button>
                          )}
                          {quick || open ? (
                            <button
                              type="button"
                              className="btn-ghost btn-sm"
                              aria-expanded={open}
                              aria-controls={panelId}
                              onClick={() => toggle(approval.id)}
                            >
                              {open ? 'Hide' : 'Details'}
                            </button>
                          ) : null}
                        </div>
                      </div>
                      {open ? (
                        <div id={panelId} className="mt-3 sm:pl-11">
                          {changed.has(approval.id) ? (
                            <p className="mb-2 text-sm text-signal" role="status">
                              This request changed since you last saw it. Review it before deciding.
                            </p>
                          ) : null}
                          <ApprovalPanel
                            approval={approval}
                            isSubmitting={isSubmitting}
                            agentName={lookups.agentName}
                            projectName={lookups.projectName}
                            taskName={lookups.taskTitle}
                            onApprove={(id, actionHash, note) =>
                              approveMutation.mutateAsync({ id, actionHash, note })
                            }
                            onReject={(id, actionHash, note) =>
                              rejectMutation.mutateAsync({ id, actionHash, note })
                            }
                            onInstruct={(id, actionHash, instruction) =>
                              instructMutation.mutateAsync({ id, actionHash, instruction })
                            }
                            onAnswer={(id, actionHash, answer) =>
                              answerMutation.mutateAsync({ id, actionHash, answer })
                            }
                          />
                        </div>
                      ) : null}
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}

type GroupKey = 'question' | 'review' | 'approval'

/** Questions come first: an agent is blocked on a plain answer. */
const GROUPS: { key: GroupKey; heading: string; label: string }[] = [
  { key: 'question', heading: 'Questions blocking a run', label: 'Questions' },
  { key: 'review', heading: 'Reviews', label: 'Reviews' },
  { key: 'approval', heading: 'Approvals', label: 'Approvals' },
]

function groupOf(approval: Approval): GroupKey {
  if (approval.type === 'agent_question') return 'question'
  if ((approval.action as { kind?: string } | undefined)?.kind === 'workflow_gate') return 'review'
  return 'approval'
}
